import type { DateTimeRange, FilterValue, SortingState } from '@e6/ds/core';
import { type ReactNode, createContext, useContext, useRef } from 'react';
import type { DurationFilterValue } from '../components/DurationFilter';
import type { QuerySummary } from '../types/queryHistory';

export interface QueryHistoryCacheState {
  timeRange: DateTimeRange;
  selectedPreset: string | null;
  columnFilters: Record<string, FilterValue>;
  sorting: SortingState;
  pageIndex: number;
  pageSize: number;
  data: QuerySummary[];
  totalCount: number;
  pageCount: number;
  durationFilter?: DurationFilterValue;
  queryIdFilter?: string[];
  cachedAt: number;
}

interface QueryHistoryCacheContextValue {
  save: (state: QueryHistoryCacheState) => void;
  getCache: () => QueryHistoryCacheState | null;
  clear: () => void;
}

const QueryHistoryCacheContext = createContext<QueryHistoryCacheContextValue | null>(null);

export function QueryHistoryCacheProvider({ children }: { children: ReactNode }) {
  const cacheRef = useRef<QueryHistoryCacheState | null>(null);

  const value: QueryHistoryCacheContextValue = {
    save: (state) => {
      cacheRef.current = state;
    },
    getCache: () => cacheRef.current,
    clear: () => {
      cacheRef.current = null;
    },
  };

  return (
    <QueryHistoryCacheContext.Provider value={value}>{children}</QueryHistoryCacheContext.Provider>
  );
}

export function useQueryHistoryCache(): QueryHistoryCacheContextValue {
  const context = useContext(QueryHistoryCacheContext);
  if (!context) {
    throw new Error('useQueryHistoryCache must be used within a QueryHistoryCacheProvider');
  }
  return context;
}
